import * as React from "react"
import { cn } from "lib/utils"

/**
 * Root Card container — elevated, rounded, responsive.
 * Automatically adapts to dark mode & hover interactions.
 */
const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "card  dark:bg-dark-800 border border-line dark:border-dark-600",
        "rounded-xl shadow-sm hover-lift transition-all duration-200 ease-in-out",
        "flex flex-col overflow-hidden",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
Card.displayName = "Card"

/**
 * CardHeader — contains title, description, and optional action area.
 */
const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-6 pt-5 pb-3 border-b border-line-subtle dark:border-dark-600",
        "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
CardHeader.displayName = "CardHeader"

/**
 * CardTitle — main heading.
 */
const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "text-lg font-semibold text-content dark:text-gray-100 leading-tight",
        className
      )}
      {...props}
    >
      {children}
    </h3>
  )
)
CardTitle.displayName = "CardTitle"

/**
 * CardDescription — subtle secondary text.
 */
const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        "text-sm text-content-muted dark:text-gray-400 leading-snug",
        className
      )}
      {...props}
    >
      {children}
    </p>
  )
)
CardDescription.displayName = "CardDescription"

/**
 * CardContent — main body of the card (form, table, etc.).
 */
const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-6 py-5 flex-1 flex flex-col gap-4", className)}
      {...props}
    >
      {children}
    </div>
  )
)
CardContent.displayName = "CardContent"

/**
 * CardFooter — actions / bottom area.
 */
const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-6 py-4 border-t border-line-subtle dark:border-dark-600 flex items-center justify-end gap-3",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
CardFooter.displayName = "CardFooter"

/**
 * CardAction — right-aligned control zone (icons, buttons)
 */
const CardAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("ml-auto flex items-center gap-2", className)}
      {...props}
    >
      {children}
    </div>
  )
)
CardAction.displayName = "CardAction"

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, CardAction }
